import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, Loader2, Sparkles, RefreshCw, XCircle, BadgePercent } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  billingAvailable,
  initBilling,
  getProducts,
  purchase as buyProduct,
  restorePurchases,
  openSubscriptionManagement,
  PRODUCT_MONTHLY,
  PRODUCT_YEARLY,
  ALL_PRODUCTS,
} from '../lib/googlePlayBilling';

// Web-only fallback prices (used until Play Store products load on Android).
const FALLBACK_PRICES = {
  [PRODUCT_MONTHLY]: { price: '$2.99', label: 'شهري' },
  [PRODUCT_YEARLY]:  { price: '$19.99', label: 'سنوي' },
};

export default function Premium() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState({}); // {productId: liveProductInfo}
  const [selectedPlan, setSelectedPlan] = useState(PRODUCT_YEARLY); // default = best value
  const isNativeAndroid = billingAvailable();

  const isPremium = !!user?.is_premium;
  const trialUsed = !!user?.trial_used;

  const perks = [
    'AI Voice Reader · قارئ صوتي',
    'Hands-free Cook Mode · وضع الطبخ',
    'AI Recipe Conversion · تحويل ذكي',
    'Calorie Tracker · تتبّع السعرات',
    'Personalized Meal Plans · خطط وجبات',
    'Ad-free experience · بدون إعلانات',
  ];

  // On native Android: connect to Play Billing and fetch live product prices.
  useEffect(() => {
    if (!isNativeAndroid) return;
    (async () => {
      const init = await initBilling();
      if (!init?.ok) return;
      const list = await getProducts(ALL_PRODUCTS);
      const byId = {};
      for (const p of list) byId[p.productId] = p;
      setProducts(byId);
    })();
  }, [isNativeAndroid]);

  const monthlyInfo = products[PRODUCT_MONTHLY];
  const yearlyInfo  = products[PRODUCT_YEARLY];

  // Compute "save XX%" badge: 12*monthly vs yearly priceAmountMicros (live data
  // from Play Store) — falls back to manually computed values when offline.
  const savePercent = useMemo(() => {
    const m = monthlyInfo?.priceAmountMicros;
    const y = yearlyInfo?.priceAmountMicros;
    if (m && y && m > 0) {
      const fullYear = m * 12;
      return Math.max(0, Math.round((1 - y / fullYear) * 100));
    }
    return 44; // 19.99 / (2.99*12) → 44%
  }, [monthlyInfo, yearlyInfo]);

  const planDisplay = (planId) => {
    const live = products[planId];
    const fb = FALLBACK_PRICES[planId];
    return {
      price: live?.price || fb.price,
      period: planId === PRODUCT_MONTHLY ? '/شهر' : '/سنة',
      label: fb.label,
      offerToken: live?.offerToken,
    };
  };
  const selected = planDisplay(selectedPlan);

  const startTrial = async () => {
    if (!user) { navigate('/login'); return; }
    setBusy(true);
    try {
      await api.post('/trial/start');
      await refresh();
      toast.success(t('trialActive') || 'تم تفعيل التجربة المجانية! 🎉');
      setTimeout(() => navigate('/tracker'), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setBusy(false); }
  };

  const subscribe = async () => {
    if (!user) { navigate('/login'); return; }
    if (!isNativeAndroid) {
      toast.info('الاشتراك متاح حالياً من تطبيق Google Play فقط 📱');
      return;
    }
    setBusy(true);
    try {
      const pr = await buyProduct(selectedPlan, selected.offerToken);
      if (!pr) { setBusy(false); return; }
      await api.post('/billing/google-play/verify', {
        purchaseToken: pr.purchaseToken,
        productId: pr.productId || selectedPlan,
        orderId: pr.orderId,
      });
      await refresh();
      toast.success('🎉 تم تفعيل اشتراك Premium!');
      setTimeout(() => navigate('/tracker'), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'فشل تفعيل الاشتراك');
    } finally { setBusy(false); }
  };

  const restore = async () => {
    if (!isNativeAndroid) {
      toast.info('استعادة المشتريات متاحة فقط داخل تطبيق Android');
      return;
    }
    setBusy(true);
    try {
      const list = await restorePurchases();
      const active = list.find(p => p.purchaseState === 1);
      if (!active) {
        toast.info('لا توجد مشتريات نشطة لاستعادتها');
        return;
      }
      await api.post('/billing/google-play/verify', {
        purchaseToken: active.purchaseToken,
        productId: active.productId || PRODUCT_MONTHLY,
        orderId: active.orderId,
      });
      await refresh();
      toast.success('✅ تمت استعادة الاشتراك بنجاح');
    } catch (e) {
      toast.error(e?.message || 'تعذر استعادة المشتريات');
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (isNativeAndroid) {
      await openSubscriptionManagement(PRODUCT_MONTHLY);
    } else {
      window.open(
        `https://play.google.com/store/account/subscriptions?package=com.matbakhi.app`,
        '_blank', 'noopener,noreferrer'
      );
    }
  };

  return (
    <div className="container max-w-5xl mx-auto px-4 py-8" data-testid="premium-page">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* ───────────── Hero card ───────────── */}
        <div className="rounded-3xl p-8 bg-gradient-to-br from-[#C05A42] to-[#8a3d2e] text-white shadow-2xl">
          <span className="inline-flex items-center gap-1 bg-white/15 px-3 py-1 rounded-full text-xs font-medium">
            <Crown className="h-4 w-4" /> Matbakhi Premium
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-display font-bold leading-tight">
            {t('premiumTitle') || 'كل ما تحتاجه لطبخ ذكي'}
          </h1>
          <p className="mt-4 text-white/85 leading-relaxed max-w-md">
            {t('premiumDesc') || 'استمتع بكل ميزات Matbakhi المتقدمة. اختر الخطة الشهرية أو السنوية بأفضل قيمة.'}
          </p>
          {isPremium && (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#7A8B76] text-white px-4 py-2 rounded-full text-sm font-medium" data-testid="premium-active">
              <Check className="h-4 w-4" /> Active
              {user?.premium_until && (
                <span className="opacity-80">· until {new Date(user.premium_until).toLocaleDateString()}</span>
              )}
            </div>
          )}
        </div>

        {/* ───────────── Plan card ───────────── */}
        <div className="glass rounded-3xl p-8 text-[#2C2420] shadow-2xl">
          <div className="text-xs uppercase tracking-wider text-[#5C4D44]">
            {t('premium') || 'Premium'} — اختر خطتك
          </div>

          {/* Plan switcher: Monthly | Yearly */}
          {!isPremium && (
            <div
              role="tablist"
              className="mt-3 grid grid-cols-2 gap-2 p-1 rounded-2xl bg-[#F1EBE3]"
              data-testid="plan-switcher"
            >
              <button
                onClick={() => setSelectedPlan(PRODUCT_MONTHLY)}
                data-testid="plan-monthly-btn"
                role="tab"
                aria-selected={selectedPlan === PRODUCT_MONTHLY}
                className={`py-2.5 rounded-xl text-sm font-semibold transition ${
                  selectedPlan === PRODUCT_MONTHLY
                    ? 'bg-white text-[#2C2420] shadow'
                    : 'text-[#5C4D44] hover:text-[#2C2420]'
                }`}
              >
                شهري
              </button>
              <button
                onClick={() => setSelectedPlan(PRODUCT_YEARLY)}
                data-testid="plan-yearly-btn"
                role="tab"
                aria-selected={selectedPlan === PRODUCT_YEARLY}
                className={`relative py-2.5 rounded-xl text-sm font-semibold transition ${
                  selectedPlan === PRODUCT_YEARLY
                    ? 'bg-white text-[#2C2420] shadow'
                    : 'text-[#5C4D44] hover:text-[#2C2420]'
                }`}
              >
                سنوي
                <span className="absolute -top-2 -end-2 bg-[#7A8B76] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  وفّر {savePercent}%
                </span>
              </button>
            </div>
          )}

          {/* Price */}
          <div className="mt-4 flex items-baseline gap-2">
            <Sparkles className="h-7 w-7 text-[#C05A42]" />
            <span className="text-4xl font-display font-bold" data-testid="premium-price">
              {selected.price}
              <span className="text-base font-normal text-[#5C4D44]"> {selected.period}</span>
            </span>
          </div>

          {/* Yearly: show monthly-equivalent helper text */}
          {selectedPlan === PRODUCT_YEARLY && (
            <p className="mt-1 text-xs text-[#5C4D44]" data-testid="yearly-equiv">
              ≈ {(parseFloat((selected.price || '$19.99').replace(/[^\d.]/g, '')) / 12).toFixed(2)}$ شهرياً ·
              أقل من فنجان قهوة
            </p>
          )}

          {/* Intro discount banner — monthly only */}
          {selectedPlan === PRODUCT_MONTHLY && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[#FFF3E0] border border-[#E29F48]/40 text-[#A04832] px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="intro-discount">
              <BadgePercent className="h-3.5 w-3.5" /> عرض الشهر الأول · خصم خاص
            </div>
          )}

          <p className="mt-3 text-sm text-[#5C4D44]">
            تجربة 7 أيام مجاناً · ألغِ في أي وقت
          </p>

          <ul className="mt-6 space-y-3" data-testid="perks-list">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#7A8B76] text-white shrink-0">
                  <Check className="h-3 w-3" />
                </span>
                {p}
              </li>
            ))}
          </ul>

          {/* ───────── Action buttons ───────── */}
          {!isPremium && !trialUsed && (
            <Button
              onClick={startTrial}
              disabled={busy}
              data-testid="start-trial-btn"
              className="mt-6 w-full rounded-full bg-[#7A8B76] hover:bg-[#62725f] text-white py-6 text-base font-semibold disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              ✨ ابدأ التجربة المجانية (7 أيام)
            </Button>
          )}

          {!isPremium && (
            <Button
              onClick={subscribe}
              disabled={busy}
              data-testid="subscribe-btn"
              className="mt-3 w-full rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white py-6 text-base font-semibold disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              💎 اشترك · {selected.price} {selected.period}
            </Button>
          )}

          {isPremium && (
            <>
              <Button
                onClick={() => navigate('/tracker')}
                data-testid="enjoy-premium-btn"
                className="mt-6 w-full rounded-full bg-[#7A8B76] hover:bg-[#62725f] text-white py-6 text-base font-semibold"
              >
                <Check className="h-4 w-4 me-2" /> استمتع بـ Premium
              </Button>
              <Button
                onClick={cancel}
                variant="outline"
                data-testid="cancel-subscription-btn"
                className="mt-3 w-full rounded-full border-[#C05A42]/40 text-[#A04832] hover:bg-[#FFF3E0] py-6 text-base font-medium"
              >
                <XCircle className="h-4 w-4 me-2" /> إلغاء الاشتراك
              </Button>
            </>
          )}

          {isNativeAndroid && (
            <Button
              onClick={restore}
              disabled={busy}
              variant="ghost"
              data-testid="restore-purchases-btn"
              className="mt-3 w-full rounded-full text-[#5C4D44] hover:bg-[#F1EBE3] py-5 text-sm font-medium"
            >
              <RefreshCw className="h-4 w-4 me-2" /> استعادة المشتريات
            </Button>
          )}

          <p className="mt-4 text-[10px] text-[#5C4D44] text-center leading-relaxed">
            بالاشتراك فأنت توافق على{' '}
            <a
              href="https://matbakhi.app"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C05A42]"
              data-testid="privacy-link"
            >
              سياسة الخصوصية
            </a>
            . يتم تجديد الاشتراك تلقائياً ويُخصم السعر من حسابك في Google Play قبل 24 ساعة من انتهاء الفترة الحالية ما لم تلغِ الاشتراك. يمكنك إلغاء الاشتراك في أي وقت من إعدادات اشتراكاتك في Google Play.
          </p>
        </div>
      </div>
    </div>
  );
}
